import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { VideoCard } from "@/components/VideoCard";
import { DiscoverButton } from "@/components/DiscoverButton";
import { ArrowRight, BookOpen, GraduationCap, Landmark, Swords, Users, MessageSquare, Clapperboard, Heart } from "lucide-react";

const HERO = "https://images.pexels.com/photos/9176052/pexels-photo-9176052.jpeg";

const STRUCTURE = [
  { icon: GraduationCap, name: "Seminars", desc: "Full-length seminars and summer camps spanning decades of instruction." },
  { icon: Swords, name: "Weapons", desc: "Aiki-ken, aiki-jo, suburi and iaido — the weapons work central to Chiba Sensei's method." },
  { icon: Clapperboard, name: "Techniques", desc: "Demonstrations and detailed technical study across the aikido curriculum." },
  { icon: MessageSquare, name: "Interviews", desc: "Conversations and reflections in Sensei's own words." },
  { icon: Landmark, name: "History", desc: "Rare and historical footage documenting the lineage, from 1964 onward." },
];

const SHOW_CONTRIBUTORS = false; // reserved — turn on once contributors have donated
const CONTRIBUTORS = [
  { name: "Liese Klein", dojo: "FireHorse Aikido" },
  { name: "Larry Levitt", dojo: "Valley Aikikai" },
  { name: "Yves Nazon", dojo: "Valley Aikido" },
  { name: "Rob Schenk", dojo: "Aikido Institute of San Francisco" },
];

export default function Landing() {
  const [videos, setVideos] = useState([]);
  const [count, setCount] = useState(0);

  useEffect(() => {
    api.get("/videos?sort=featured").then(({ data }) => {
      setVideos(data.videos.slice(0, 6));
      setCount(data.count);
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen grain">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img src={HERO} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0B0E]/60 via-[#0A0B0E]/80 to-[#0A0B0E]" />
        </div>
        <div data-testid="beta-notice" className="relative px-6 md:px-12 lg:px-24 mx-auto max-w-7xl pt-20 md:pt-28 pb-20 md:pb-28 text-center fade-up">
          <p className="font-serif text-2xl sm:text-3xl lg:text-4xl leading-tight text-[#EBE9E1]">
            <span className="text-[#C5A059] font-semibold">BETA TEST SITE</span> — Proof of Concept
          </p>
          <p className="mt-3 text-base sm:text-lg text-[#8A8F98]">
            Created for the Board and Senior Council Only.
          </p>
        </div>
        <div className="relative px-6 md:px-12 lg:px-24 mx-auto max-w-7xl pb-32 md:pb-44">
          <p className="text-sm tracking-[0.35em] uppercase text-[#C5A059] mb-6 fade-up">Presented by Birankai North America</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] text-[#EBE9E1] max-w-4xl fade-up" style={{ animationDelay: "80ms" }}>
            The Official<br /><span className="text-[#C5A059]">T.K. Chiba Shihan</span> Archive
          </h1>
          <p className="mt-8 text-lg text-[#8A8F98] max-w-xl leading-relaxed fade-up" style={{ animationDelay: "160ms" }}>
            A living archive dedicated to preserving, teaching, and honouring the lineage
            of the late Kazuo (T.K.) Chiba Shihan — his seminars, weapons work, techniques,
            interviews, and history, safeguarded for students today and for generations to come.
          </p>
          <div className="mt-12 flex flex-wrap items-center gap-5 fade-up" style={{ animationDelay: "240ms" }}>
            <Link to="/register" data-testid="hero-join-btn" className="btn-primary px-8 py-4 font-medium flex items-center gap-2">
              Create Free Account <ArrowRight size={18} />
            </Link>
            <Link to="/library" data-testid="hero-browse-btn" className="btn-ghost px-8 py-4">
              Browse the Library
            </Link>
            <DiscoverButton variant="ghost" />
          </div>
          <p className="mt-10 text-sm text-[#8A8F98] fade-up" style={{ animationDelay: "320ms" }}>
            {count} recordings available to view · New material added regularly
          </p>
        </div>
      </section>

      {/* Mission statement */}
      <section className="px-6 md:px-12 lg:px-24 mx-auto max-w-4xl py-24 md:py-32 border-t border-white/10 text-center">
        <p className="text-sm tracking-[0.3em] uppercase text-[#C5A059] mb-8 fade-up">Our Mission</p>
        <p className="font-serif text-3xl md:text-4xl lg:text-[2.75rem] leading-[1.25] text-[#EBE9E1] fade-up" style={{ animationDelay: "80ms" }}>
          To preserve, organise, and make accessible the recorded teachings of
          <span className="text-[#C5A059]"> Kazuo (T.K.) Chiba Shihan</span> — safeguarding a
          living record of his aikido for students today and for generations yet to train.
        </p>
      </section>

      {/* Short biography + why it exists */}
      <section className="px-6 md:px-12 lg:px-24 mx-auto max-w-7xl py-20 border-t border-white/10 grid lg:grid-cols-2 gap-16 items-start">
        <div className="fade-up">
          <p className="text-sm tracking-[0.3em] uppercase text-[#C5A059] mb-5">Who Was Chiba Sensei</p>
          <h2 className="font-serif text-3xl md:text-4xl text-[#EBE9E1] mb-6">A Direct Student of the Founder</h2>
          <div className="space-y-4 text-[#EBE9E1]/80 leading-relaxed text-lg">
            <p>
              Kazuo Chiba Sensei (1940–2015) entered Aikikai Hombu Dojo in 1958 as a live-in
              student (uchideshi) of the Founder of Aikido, Morihei Ueshiba O-Sensei — among the
              last generation to train at his side.
            </p>
            <p>
              An 8th Dan, he carried the art to the United Kingdom and later to
              California, founding San Diego Aikikai and, in time, Birankai. His teaching was known
              for its rigor, depth, and intensity.
            </p>
          </div>
          <Link to="/about" data-testid="mission-about-link" className="inline-flex items-center gap-2 text-[#C5A059] hover:text-[#D6B570] transition-colors mt-6">
            Read the full history <ArrowRight size={16} />
          </Link>
        </div>
        <div className="fade-up" style={{ animationDelay: "100ms" }}>
          <p className="text-sm tracking-[0.3em] uppercase text-[#C5A059] mb-5">Why This Archive Exists</p>
          <h2 className="font-serif text-3xl md:text-4xl text-[#EBE9E1] mb-6">Before the Record Is Lost</h2>
          <div className="space-y-4 text-[#EBE9E1]/80 leading-relaxed text-lg">
            <p>
              Much of Chiba Sensei's teaching survives only on ageing tapes and scattered private
              collections. This archive gathers that material into one carefully catalogued place —
              recording dates, locations, sources, and rights — so the footage is not merely stored,
              but understood.
            </p>
            <p>
              Presented by Birankai North America, it exists to protect this legacy and to serve the
              students who continue his practice.
            </p>
            <p className="text-sm text-[#8A8F98]">
              The Archive includes embedded or linked material from publicly available sources as well
              as material provided directly by contributors. Copyright remains with the respective
              rights holders. Rights, provenance, and attribution are being researched and documented
              as part of the archival process.
            </p>
          </div>
        </div>
      </section>


      {/* Mission */}
      <section className="px-6 md:px-12 lg:px-24 mx-auto max-w-7xl py-20 border-t border-white/10 grid md:grid-cols-3 gap-12">
        {[
          { icon: BookOpen, t: "Preservation", d: "Safeguarding rare footage of a master's teaching before it is lost — carefully catalogued and archived." },
          { icon: GraduationCap, t: "Education", d: "A study resource for aikidoka worldwide: seminars, weapons, techniques, interviews and history in one place." },
          { icon: Heart, t: "Lineage", d: "Carrying forward the transmission of Chiba Sensei's aikido within the Birankai tradition." },
        ].map(({ icon: Icon, t, d }, i) => (
          <div key={t} className="fade-up" style={{ animationDelay: `${i * 80}ms` }}>
            <Icon size={28} strokeWidth={1.5} className="text-[#C5A059] mb-5" />
            <h3 className="font-serif text-2xl text-[#EBE9E1] mb-3">{t}</h3>
            <p className="text-[#8A8F98] leading-relaxed">{d}</p>
          </div>
        ))}
      </section>

      {/* Library structure */}
      <section className="px-6 md:px-12 lg:px-24 mx-auto max-w-7xl py-20 border-t border-white/10">
        <p className="text-sm tracking-[0.3em] uppercase text-[#C5A059] mb-3">How the Archive is Organised</p>
        <h2 className="font-serif text-4xl text-[#EBE9E1] mb-12">Five Areas of Study</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
          {STRUCTURE.map(({ icon: Icon, name, desc }, i) => (
            <Link key={name} to={`/library?category=${name}`} data-testid={`structure-${name}`}
              className="border border-white/10 bg-[#121418] p-6 hover:border-[#C5A059]/50 transition-colors fade-up group"
              style={{ animationDelay: `${i * 60}ms` }}>
              <Icon size={24} strokeWidth={1.5} className="text-[#C5A059] mb-4" />
              <h3 className="font-serif text-xl text-[#EBE9E1] mb-2 group-hover:text-[#C5A059] transition-colors">{name}</h3>
              <p className="text-[#8A8F98] text-sm leading-relaxed">{desc}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured grid — a look at the member experience */}
      <section className="px-6 md:px-12 lg:px-24 mx-auto max-w-7xl py-20 border-t border-white/10">
        <div className="flex items-end justify-between mb-4">
          <div>
            <p className="text-sm tracking-[0.3em] uppercase text-[#C5A059] mb-3">Inside the Archive</p>
            <h2 className="font-serif text-4xl text-[#EBE9E1]">Recently Featured</h2>
          </div>
          <Link to="/library" className="hidden md:flex items-center gap-2 text-[#8A8F98] hover:text-[#C5A059] transition-colors">
            View all <ArrowRight size={16} />
          </Link>
        </div>
        <p className="text-[#8A8F98] max-w-2xl mb-12">This is a beta for the Board and Senior Council. Sign in to stream the available recordings on any device, with new material featured in the "This Week from the Archive" email.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {videos.map((v, i) => <VideoCard key={v.id} video={v} index={i} />)}
        </div>
      </section>

      {/* Contributors — hidden until contributions are made (flip SHOW_CONTRIBUTORS to true) */}
      {SHOW_CONTRIBUTORS && (
      <section className="px-6 md:px-12 lg:px-24 mx-auto max-w-7xl py-20 border-t border-white/10">
        <Users size={28} strokeWidth={1.5} className="text-[#C5A059] mb-5" />
        <h2 className="font-serif text-4xl text-[#EBE9E1] mb-4">With Gratitude to Our Contributors</h2>
        <p className="text-[#8A8F98] max-w-2xl leading-relaxed mb-8">
          This archive exists thanks to the students, instructors, and custodians who have shared
          their recordings and collections with the Birankai community — including those below and many others.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {CONTRIBUTORS.map((c) => (
            <div key={c.name} className="border border-white/10 bg-[#121418] p-5">
              <p className="font-serif text-xl text-[#EBE9E1]">{c.name}</p>
              {c.dojo && <p className="text-sm text-[#C5A059] mt-1">{c.dojo}</p>}
            </div>
          ))}
        </div>
        <Link to="/about" data-testid="contributors-about-link" className="inline-flex items-center gap-2 text-[#8A8F98] hover:text-[#C5A059] transition-colors mt-8">
          Read about Birankai &amp; Chiba Sensei <ArrowRight size={16} />
        </Link>
      </section>
      )}

      {/* Footer with rights + BNA */}
      <footer className="px-6 md:px-12 lg:px-24 mx-auto max-w-7xl py-16 border-t border-white/10 text-[#8A8F98] text-sm space-y-3">
        <p className="font-serif text-xl text-[#EBE9E1]">Official T.K. Chiba Shihan Archive</p>
        <p>Presented by Birankai North America in support of its preservation and education mission.</p>
        <p>In memory of Kazuo (T.K.) Chiba Shihan, 1940–2015.</p>
        <p className="text-[#555] max-w-3xl pt-3">
          Content is included by permission of the copyright holders or under archive contributor agreement.
          Additional material is added as rights are confirmed. All rights remain with their respective owners.
        </p>
      </footer>
    </div>
  );
}
